import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { FileDown, FileText } from "lucide-react";
import LogoFallback from "/assets/img/bubbanlogo.jpg";

export default function CareerDetails({
  logoSrc = LogoFallback,
  accent = "#115e32",
  pageTitle = "Career | Bandarban University",
}) {
	const { id } = useParams(); // Assuming dynamic route like /career/:id
	const [career, setCareer] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
	const fetchCareerDetails = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
				id:id,
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },				
			});
			const response = await api.post("/career-details", payload);
			
			
			setCareer(response.data[0]);
			
		} catch (err) {
		   setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
  
	useEffect(() => {
		fetchCareerDetails();
	}, [id]);
	useEffect(() => {
		document.title = pageTitle;
	}, [pageTitle]);
	if (loading) {
		return <p className="text-center py-20 text-gray-500">Loading...</p>;
	}

	if (error) {
		return <p className="text-center py-20 text-red-500">{error}</p>;
	}

	if (!career) {
		return <p className="text-center py-20 text-gray-500">No data found.</p>;
	}
	const categories = ["Any", "Academic", "Non Academic"];
  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-green-700 to-teal-600 text-white py-16 text-center">
        <h1 className="text-4xl font-bold">{career.designation_name} in {career.department_name}</h1>
        <p className="mt-2 text-lg opacity-90">Career Opportunity Details</p>
      </section>

      {/* Career Details */}
      <section className="max-w-5xl mx-auto py-16 px-6">
        <div className="bg-white shadow-xl rounded-2xl p-8 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Detail label="Number of Position" value={career.position_number} />
            <Detail label="Gender" value={career.gender && career.gender.trim() !== "" ? career.gender : "For All"} />
            <Detail label="Department" value={career.department_name} />
            <Detail label="Designation" value={career.designation_name} />
            <Detail
              label="Opening Date"
              value={new Date(career.start_date).toLocaleDateString()}
            />
            <Detail
              label="Deadline"
              value={new Date(career.end_date).toLocaleDateString()}
            />
            <Detail label="Job Type" value={categories[career.job_type]} />
            <Detail label="Specialization" value={career.subject && career.subject.trim() !== "" ? career.subject : "Relevant"} />
          </div>

          <div>
            <h3 className="font-bold text-lg text-teal-700 mb-2">Qualification</h3>
            <p className="text-gray-700 whitespace-pre-line">{career.qualification}</p>
          </div>

          <div>
            <h3 className="font-bold text-lg text-teal-700 mb-2">Responsibilities</h3>
            <p className="text-gray-700 whitespace-pre-line">{career.responsibilites}</p>
          </div>

          <div>
            <h3 className="font-bold text-lg text-teal-700 mb-2">
              Additional Requirement / Information
            </h3>
            <p className="text-gray-700 whitespace-pre-line">
              {career.additional_requirement}
            </p>
          </div>

          {/* Buttons */}
          <div className="flex flex-wrap gap-4 pt-6">
            <a
              href={`${import.meta.env.VITE_API_SOURCE_URL}assets/files/Application-Form.doc`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-teal-600 text-white px-5 py-3 rounded-xl hover:bg-teal-700 transition"
            >
              <FileText className="w-5 h-5" />
              Download Application Form
            </a>
            <a
              href={`${import.meta.env.VITE_API_SOURCE_URL}${career.advertisement}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-green-600 text-white px-5 py-3 rounded-xl hover:bg-green-700 transition"
            >
              <FileDown className="w-5 h-5" />
              Download Job Advertisement
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      <p className="text-gray-800 font-medium">{value || "—"}</p>
    </div>
  );
}
